import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["star", "input", "display", "container"]
  static values = { allowHalf: { type: Boolean, default: true } }

  connect() {
    this.hoveredValue = 0
    this.selectedValue = parseFloat(this.inputTarget?.value) || 0

    // Check if we're using the new button-based approach (for whiskey ratings)
    if (this.hasContainerTarget) {
      this.updateStars(this.selectedValue)
    } else {
      // Old approach with star targets (for flight ratings)
      this.starTargets.forEach((star) => {
        star.addEventListener('mouseenter', () => this.hover(star))
        star.addEventListener('click', () => this.select(star))
      })
      this.element.addEventListener('mouseleave', () => this.reset())
      this.updateStars(this.selectedValue)
    }
  }

  // New method for button-based stars (whiskey ratings)
  setStar(event) {
    event.preventDefault()
    const starIndex = parseInt(event.currentTarget.dataset.starIndex)
    const currentValue = parseFloat(this.inputTarget.value) || 0

    let newValue

    if (this.allowHalfValue) {
      // Support half stars
      if (currentValue === starIndex) {
        // Full star clicked again - make it half
        newValue = starIndex - 0.5
      } else if (currentValue === starIndex - 0.5) {
        // Half star clicked - make it full
        newValue = starIndex
      } else {
        // New star clicked - make it full
        newValue = starIndex
      }
    } else {
      // Integer only
      newValue = starIndex
    }

    this.inputTarget.value = newValue
    this.selectedValue = newValue
    this.updateStars(newValue)
  }

  // Old method for radio button stars (flight ratings)
  select(star) {
    const starIndex = parseInt(star.dataset.value)
    const currentValue = this.selectedValue

    let value

    if (this.allowHalfValue) {
      // Support half stars on click
      if (currentValue === starIndex) {
        value = starIndex - 0.5
      } else if (currentValue === starIndex - 0.5) {
        value = starIndex
      } else {
        value = starIndex
      }
    } else {
      value = starIndex
    }

    this.selectedValue = value

    // Update the input (radio button or hidden field)
    if (this.inputTarget.type === 'radio') {
      // Find closest radio button
      const input = this.inputTargets.find(i => parseFloat(i.value) === Math.round(value))
      if (input) {
        input.checked = true
      }
    } else {
      this.inputTarget.value = value
    }

    this.updateStars(value)
  }

  hover(star) {
    const value = parseInt(star.dataset.value)
    this.hoveredValue = value
    this.updateStars(value)
  }

  reset() {
    this.hoveredValue = 0
    this.updateStars(this.selectedValue)
  }

  updateStars(value) {
    const fullStars = Math.floor(value)
    const hasHalfStar = this.allowHalfValue && (value % 1 >= 0.5)

    // Update display if present
    if (this.hasDisplayTarget) {
      this.displayTarget.textContent = value.toFixed(1)
    }

    // New button-based stars (whiskey ratings)
    if (this.hasContainerTarget) {
      const stars = this.containerTarget.querySelectorAll('.star-button')
      stars.forEach((star, index) => {
        const icon = star.querySelector('svg')
        if (!icon) return

        if (index < fullStars) {
          // Full star - filled with primary color
          icon.classList.remove('text-muted-foreground')
          icon.classList.add('text-primary')
          icon.setAttribute('fill', 'currentColor')
        } else if (index === fullStars && hasHalfStar) {
          // Half star indicator - outlined in primary color (no fill)
          icon.classList.remove('text-muted-foreground')
          icon.classList.add('text-primary')
          icon.setAttribute('fill', 'none')
        } else {
          // Empty star - outlined in muted color
          icon.classList.remove('text-primary')
          icon.classList.add('text-muted-foreground')
          icon.setAttribute('fill', 'none')
        }
      })
    }

    // Old star targets approach (flight ratings)
    if (this.hasStarTarget) {
      this.starTargets.forEach((star, index) => {
        const starValue = index + 1
        const polygon = star.querySelector('polygon')

        if (starValue <= fullStars) {
          // Full star
          star.classList.add('text-gold')
          star.classList.remove('text-muted-foreground')
          if (polygon) {
            polygon.setAttribute('fill', 'currentColor')
          }
        } else if (starValue === fullStars + 1 && hasHalfStar) {
          // Half star
          star.classList.add('text-gold')
          star.classList.remove('text-muted-foreground')
          if (polygon) {
            polygon.setAttribute('fill', 'url(#half-star-gradient)')
          }
        } else {
          // Empty star
          star.classList.remove('text-gold')
          star.classList.add('text-muted-foreground')
          if (polygon) {
            polygon.setAttribute('fill', 'none')
          }
        }
      })
    }
  }
}
